import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Users, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/districts`;

export const districtSlug = (d) => encodeURIComponent(d.toLowerCase());

export default function Districts() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    fetch(API).then(r => r.json()).then(setRows).catch(() => setRows([]));
  }, []);

  const list = useMemo(() => (rows || []).filter((r) => r.district.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const activeCount = (rows || []).filter((r) => r.active).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="districts-page">
      <section className="bg-gradient-to-br from-party-green via-emerald-700 to-gray-900 py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-green-200">{t({ en: 'District Chapters', bn: 'জেলা শাখা' })}</p>
          <h1 className="text-4xl font-bold text-white sm:text-5xl lg:text-6xl">{t({ en: 'Find NDP near you', bn: 'আপনার কাছের এনডিপি খুঁজুন' })}</h1>
          <p className="mt-4 max-w-xl text-green-100">{t({ en: `64 districts. ${activeCount} active chapters. Find your local office and leaders, or help start one.`, bn: `৬৪ জেলা। ${activeCount}টি সক্রিয় শাখা। আপনার স্থানীয় অফিস ও নেতাদের খুঁজুন, অথবা একটি শুরু করতে সাহায্য করুন।` })}</p>
          <div className="mt-8 flex max-w-md items-center gap-2 rounded-full bg-white px-4 py-3 shadow-lg">
            <Search className="h-5 w-5 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t({ en: 'Search district…', bn: 'জেলা খুঁজুন…' })} className="flex-1 bg-transparent text-base outline-none" data-testid="district-search" />
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-6xl px-4 py-10">
        {!rows ? <Loader2 className="mx-auto h-8 w-8 animate-spin text-party-red" /> : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="district-grid">
            {list.map((r) => (
              <button key={r.district} onClick={() => navigate(`/districts/${districtSlug(r.district)}`)} data-testid={`district-card-${r.district.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className="group rounded-2xl border bg-white p-4 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg">
                <div className="mb-2 flex items-center justify-between">
                  <MapPin className="h-4 w-4 text-party-red" />
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.active ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {r.active ? t({ en: 'Active', bn: 'সক্রিয়' }) : t({ en: 'Forming', bn: 'গঠনাধীন' })}
                  </span>
                </div>
                <p className="font-bold text-gray-900 group-hover:text-party-red">{r.district}</p>
                <p className="mt-1 flex items-center gap-1 text-xs text-gray-500"><Users className="h-3 w-3" /> {r.members} {t({ en: 'members', bn: 'সদস্য' })}</p>
              </button>
            ))}
            {list.length === 0 && <p className="col-span-full text-center text-gray-500" data-testid="district-no-results">{t({ en: 'No district matches.', bn: 'কোনো জেলা মেলেনি।' })}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
